import { motion, useInView, AnimatePresence } from "framer-motion";
import { useRef, useState, useEffect, useCallback } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Search, Globe, Rocket, BarChart3, TrendingUp } from "lucide-react";
import methodImg01 from "@/assets/methodology-01-v3.webp";
import methodImg02 from "@/assets/methodology-02-v3.jpg";
import methodImg03 from "@/assets/methodology-03-v3.jpg";
import methodImg04 from "@/assets/methodology-04-v3.jpg";
import methodImg05 from "@/assets/methodology-05-v3.jpg";

const steps = [
  { id: "diagnostico", num: "01", icon: Search, labelKey: "methodology.tab1.label", descKey: "methodology.tab1.desc", img: methodImg01, color: "#E92052" },
  { id: "estrategia", num: "02", icon: Rocket, labelKey: "methodology.tab2.label", descKey: "methodology.tab2.desc", img: methodImg03, color: "#602d90" },
  { id: "ecosistema", num: "03", icon: Globe, labelKey: "methodology.tab3.label", descKey: "methodology.tab3.desc", img: methodImg02, color: "#fda700" },
  { id: "analisis", num: "04", icon: BarChart3, labelKey: "methodology.tab4.label", descKey: "methodology.tab4.desc", img: methodImg04, color: "#ffffff" },
  { id: "crecimiento", num: "05", icon: TrendingUp, labelKey: "methodology.tab5.label", descKey: "methodology.tab5.desc", img: methodImg05, color: "#E92052" },
];

const AUTOPLAY_INTERVAL = 1500;

const MethodologySection = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: false, margin: "-20%" });
  const sectionVisible = useInView(ref, { once: true, margin: "-80px" });
  const { t } = useLanguage();
  const [activeIdx, setActiveIdx] = useState(0);
  const [userInteracted, setUserInteracted] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startAutoplay = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setActiveIdx((prev) => (prev + 1) % steps.length);
    }, AUTOPLAY_INTERVAL);
  }, []);

  // Auto-advance when section is in view
  useEffect(() => {
    if (inView && !userInteracted) {
      startAutoplay();
    } else {
      if (timerRef.current) clearInterval(timerRef.current);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [inView, userInteracted, startAutoplay]);

  // Reset autoplay after user interaction (resume after 8s)
  const handleManualClick = (idx: number) => {
    setActiveIdx(idx);
    setUserInteracted(true);
    if (timerRef.current) clearInterval(timerRef.current);
    setTimeout(() => {
      setUserInteracted(false);
    }, 4000);
  };

  return (
    <section
      id="metodologia"
      className="relative py-20 md:py-32 text-foreground"
      ref={ref}
      style={{ backgroundColor: "#f7f7f7" }}
    >
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <motion.div
          className="mb-12 md:mb-16"
          initial={{ opacity: 0, y: 40, filter: "blur(8px)" }}
          animate={sectionVisible ? { opacity: 1, y: 0, filter: "blur(0px)" } : {}}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        >
          <p className="text-xs uppercase tracking-[0.25em] font-bold text-brand-rosa mb-4 text-popover-foreground">
            {t("methodology.tag")}
          </p>
          <h2 className="text-3xl md:text-5xl lg:text-6xl font-black tracking-tight lowercase text-foreground leading-[0.95]">
            {t("methodology.title")}{" "}
            <span className="text-brand-rosa">growth</span>
          </h2>
          <p className="mt-4 text-base md:text-lg text-muted-foreground max-w-3xl leading-relaxed">
            {t("methodology.subtitle")}
          </p>
        </motion.div>

        {/* Card */}
        <motion.div
          className="rounded-[1.25rem] overflow-hidden shadow-lg"
          style={{ backgroundColor: "#0D0D0D" }}
          initial={{ opacity: 0, y: 40 }}
          animate={sectionVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
        >
          {steps.map((step, i) => {
            const isActive = activeIdx === i;
            const Icon = step.icon;

            return (
              <div key={step.id}>
                {/* Header row */}
                <button
                  onClick={() => handleManualClick(i)}
                  className="w-full text-left px-8 md:px-12 py-5 md:py-6 flex items-center gap-5 md:gap-8 transition-all duration-500 hover:bg-white/[0.04] group"
                >
                  <motion.span
                    className="text-sm font-black tracking-wider"
                    animate={{ color: isActive ? step.color : "rgba(255,255,255,0.2)" }}
                    transition={{ duration: 0.5 }}
                  >
                    {step.num}
                  </motion.span>

                  <motion.div
                    animate={{ color: isActive ? step.color : "rgba(255,255,255,0.2)", scale: isActive ? 1 : 0.85, opacity: isActive ? 1 : 0.4 }}
                    transition={{ duration: 0.4 }}
                  >
                    <Icon size={18} />
                  </motion.div>

                  <motion.span
                    className="text-sm md:text-base font-black uppercase tracking-[0.15em]"
                    animate={{ color: isActive ? "#ffffff" : "rgba(255,255,255,0.25)" }}
                    transition={{ duration: 0.5 }}
                  >
                    {t(step.labelKey)}
                  </motion.span>

                  {/* Progress dot */}
                  <motion.div
                    className="ml-auto w-2 h-2 rounded-full"
                    animate={{
                      backgroundColor: isActive ? step.color : "rgba(255,255,255,0.1)",
                      scale: isActive ? 1 : 0.5,
                    }}
                    transition={{ duration: 0.4 }}
                  />
                </button>

                {/* Expanded content */}
                <AnimatePresence initial={false}>
                  {isActive && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{
                        height: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
                        opacity: { duration: 0.5, ease: "easeInOut" },
                      }}
                      className="overflow-hidden"
                    >
                      <div className="px-8 md:px-12 pb-10 md:pb-14">
                        <div className="grid md:grid-cols-[1fr_1fr] gap-8 md:gap-12 items-center">
                          {/* Left: Description */}
                          <motion.div
                            initial={{ opacity: 0, x: -20, filter: "blur(8px)" }}
                            animate={{ opacity: 1, x: 0, filter: "blur(0px)" }}
                            transition={{ duration: 0.8, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
                          >
                            <p className="text-base md:text-lg leading-relaxed max-w-lg" style={{ color: "rgba(255,255,255,0.55)" }}>
                              {t(step.descKey)}
                            </p>
                          </motion.div>

                          {/* Right: Square image with crossfade */}
                          <motion.div
                            className="flex justify-center md:justify-end"
                            initial={{ opacity: 0, scale: 0.92, filter: "blur(12px)" }}
                            animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
                            transition={{ duration: 1, delay: 0.05, ease: [0.22, 1, 0.36, 1] }}
                            
                          >
                            <div
                              className="w-64 h-64 md:w-80 md:h-80 lg:w-[22rem] lg:h-[22rem] rounded-2xl overflow-hidden"
                              style={{
                                border: `2px solid ${step.color}40`,
                                boxShadow: `0 0 40px 8px ${step.color}15`,
                              }}
                            >
                              <img
                                src={step.img}
                                alt={t(step.labelKey)}
                                width={450}
                                height={450}
                                className="w-full h-full object-cover"
                                loading="lazy"
                                decoding="async"
                              />
                            </div>
                          </motion.div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Divider */}
                {i < steps.length - 1 && (
                  <motion.div
                    className="mx-8 md:mx-12 h-[1px]"
                    animate={{ backgroundColor: isActive ? `${step.color}30` : "rgba(255,255,255,0.06)" }}
                    transition={{ duration: 0.5 }}
                  />
                )}
              </div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

export default MethodologySection;
